<script>
  import { createEventDispatcher } from 'svelte';
  import { projectStore } from '../../stores/project';
  import { workOrderStore } from '../../stores/work-order';
  import { worker } from '../../stores/worker';
  import { clientStore } from '../../stores/client';
  import { isMobile } from '../../stores/screenSize';

  const dispatch = createEventDispatcher();

  $: projects = $projectStore.items || [];
  $: workOrders = $workOrderStore.items || [];
  $: workers = $worker.workers || [];
  $: clients = $clientStore.items || [];
  $: loading = $projectStore.loading;

  function handleAddTask(projectId) {
    dispatch('addTask', { projectId });
  }

  function handleMessage(projectId, clientId) {
    dispatch('message', { projectId, clientId });
  }

  function handleLogTime(woId) {
    dispatch('logTime', { workOrderId: woId });
  }

  function handleAddNote(woId) {
    dispatch('addNote', { workOrderId: woId });
  }

  async function handleComplete(woId) {
    try {
      await workOrderStore.update(woId, { status: 'COMPLETED' });
    } catch (e) {
      console.error('Failed to complete work order', e);
    }
  }

  async function handleChangeClient(projectId, newClientId) {
    try {
      await projectStore.update(projectId, { client: newClientId });
    } catch (e) {
      console.error('Failed to update client', e);
    }
  }

  async function handleStatusChange(projectId, newStatus) {
    try {
      await projectStore.update(projectId, { status: newStatus });
    } catch (e) {
      console.error('Failed to update status', e);
    }
  }

  let search = '';
  let expanded = new Set();
  let filter = 'all';
  let editingClient = null;

  $: filtered = projects.filter(p => {
    if (filter === 'active' && p.status !== 'IN_PROGRESS') return false;
    if (filter === 'planning' && p.status !== 'PLANNING') return false;
    if (filter === 'completed' && p.status !== 'COMPLETED') return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return p.name?.toLowerCase().includes(s) || p.client_name?.toLowerCase().includes(s);
  });

  function toggleExpand(id) {
    if (expanded.has(id)) expanded.delete(id);
    else expanded.add(id);
    expanded = expanded;
  }

  function getProjectWorkOrders(projectId) {
    return workOrders.filter(wo => wo.project === projectId);
  }

  function getClient(clientId) {
    return clients.find(c => c.id === clientId);
  }

  function statusConfig(status) {
    if (status === 'COMPLETED') return { label: 'Completed', class: 'status-success' };
    if (status === 'IN_PROGRESS') return { label: 'Active', class: 'status-active' };
    if (status === 'PLANNING') return { label: 'Planning', class: 'status-warning' };
    return { label: status || 'Unknown', class: 'status-default' };
  }

  function priorityConfig(priority) {
    if (priority === 'URGENT') return { label: 'Urgent', class: 'priority-urgent' };
    if (priority === 'HIGH') return { label: 'High', class: 'priority-high' };
    return { label: priority || 'Normal', class: 'priority-normal' };
  }

  function formatCurrency(val) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
  }

  function formatDate(date) {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  $: activeCount = projects.filter(p => p.status === 'IN_PROGRESS').length;
  $: totalBudget = projects.reduce((sum, p) => sum + (p.budget || 0), 0);
</script>

<div class="project-hub">
  <!-- Command Bar -->
  <div class="command-bar">
    <div class="search-box">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
      <input type="text" placeholder="Search projects..." bind:value={search} />
    </div>
    <div class="filters">
      <button class:active={filter === 'all'} on:click={() => filter = 'all'}>All</button>
      <button class:active={filter === 'active'} on:click={() => filter = 'active'}>Active</button>
      <button class:active={filter === 'planning'} on:click={() => filter = 'planning'}>Planning</button>
      <button class:active={filter === 'completed'} on:click={() => filter = 'completed'}>Done</button>
    </div>
  </div>

  <!-- Quick Stats -->
  <div class="stats-row">
    <div class="stat">
      <span class="stat-value">{activeCount}</span>
      <span class="stat-label">Active</span>
    </div>
    <div class="stat">
      <span class="stat-value">{filtered.length}</span>
      <span class="stat-label">Total</span>
    </div>
    <div class="stat">
      <span class="stat-value">{formatCurrency(totalBudget)}</span>
      <span class="stat-label">Budget</span>
    </div>
  </div>

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
    </div>
  {:else if filtered.length === 0}
    <div class="empty-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
      </svg>
      <p>No projects found</p>
    </div>
  {:else}
    <div class="project-grid">
      {#each filtered as project (project.id)}
        {@const projectWOs = getProjectWorkOrders(project.id)}
        {@const client = getClient(project.client)}
        {@const status = statusConfig(project.status)}
        {@const isExpanded = expanded.has(project.id)}
        {@const activeWOs = projectWOs.filter(wo => wo.status !== 'COMPLETED').length}

        <div class="project-card" class:expanded={isExpanded}>
          <!-- Card Header -->
          <div class="card-header" on:click={() => toggleExpand(project.id)} on:keydown={() => {}} role="button" tabindex="0">
            <div class="header-main">
              <div class="project-title">
                <h3>{project.name}</h3>
                <span class="status-badge {status.class}">{status.label}</span>
              </div>
              <div class="project-meta">
                {#if client}
                  <span class="client-tag">{client.name}</span>
                {/if}
                {#if activeWOs > 0}
                  <span class="wo-count">{activeWOs} active task{activeWOs !== 1 ? 's' : ''}</span>
                {/if}
              </div>
            </div>
            <div class="header-right">
              <div class="progress-ring" style="--progress: {project.progress || 0}">
                <span>{project.progress || 0}%</span>
              </div>
              <svg class="expand-icon" class:rotated={isExpanded} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <!-- Expandable Content -->
          {#if isExpanded}
            <div class="card-content">
              <!-- Key Metrics -->
              <div class="metrics-grid">
                <div class="metric">
                  <span class="metric-label">Client</span>
                  <select class="inline-select" value={project.client || ''} on:change={(e) => handleChangeClient(project.id, parseInt(e.target.value))} on:click|stopPropagation>
                    <option value="">No client</option>
                    {#each clients as c}
                      <option value={c.id}>{c.name}</option>
                    {/each}
                  </select>
                </div>
                <div class="metric">
                  <span class="metric-label">Status</span>
                  <select class="inline-select" value={project.status || 'PLANNING'} on:change={(e) => handleStatusChange(project.id, e.target.value)} on:click|stopPropagation>
                    <option value="PLANNING">Planning</option>
                    <option value="IN_PROGRESS">In Progress</option>
                    <option value="ON_HOLD">On Hold</option>
                    <option value="COMPLETED">Completed</option>
                  </select>
                </div>
                <div class="metric">
                  <span class="metric-label">Budget</span>
                  <span class="metric-value">{formatCurrency(project.budget)}</span>
                </div>
                <div class="metric">
                  <span class="metric-label">Timeline</span>
                  <span class="metric-value">
                    {formatDate(project.start_date)} → {formatDate(project.end_date)}
                  </span>
                </div>
              </div>

              <!-- Work Orders Section -->
              {#if projectWOs.length > 0}
                <div class="section">
                  <div class="section-header">
                    <h4>Work Orders</h4>
                    <span class="section-count">{projectWOs.length}</span>
                  </div>
                  <div class="wo-list">
                    {#each projectWOs.slice(0, 5) as wo}
                      {@const woStatus = statusConfig(wo.status)}
                      {@const woPriority = priorityConfig(wo.priority)}
                      <div class="wo-item">
                        <div class="wo-main">
                          <span class="wo-title">{wo.title}</span>
                          <div class="wo-meta">
                            <span class="status-dot {woStatus.class}"></span>
                            <span>{woStatus.label}</span>
                            {#if wo.priority === 'URGENT' || wo.priority === 'HIGH'}
                              <span class="priority-tag {woPriority.class}">{woPriority.label}</span>
                            {/if}
                          </div>
                        </div>
                        <div class="wo-actions">
                          <button class="wo-action" title="Log time" on:click|stopPropagation={() => handleLogTime(wo.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </button>
                          <button class="wo-action" title="Add note" on:click|stopPropagation={() => handleAddNote(wo.id)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          {#if wo.status !== 'COMPLETED'}
                            <button class="wo-action complete" title="Mark complete" on:click|stopPropagation={() => handleComplete(wo.id)}>
                              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                                <path d="M5 13l4 4L19 7" />
                              </svg>
                            </button>
                          {/if}
                        </div>
                      </div>
                    {/each}
                    {#if projectWOs.length > 5}
                      <div class="more-link">+{projectWOs.length - 5} more</div>
                    {/if}
                  </div>
                </div>
              {:else}
                <div class="empty-section">No work orders yet</div>
              {/if}

              <!-- Client Details (collapsed by default) -->
              {#if client}
                <details class="details-section">
                  <summary class="clickable-summary">
                    <div class="summary-left">
                      <span>Client Details</span>
                      <span class="tap-hint">tap to expand</span>
                    </div>
                    <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div class="details-content">
                    <div class="detail-row">
                      <span class="detail-label">Email</span>
                      <a href="mailto:{client.email}" class="detail-value">{client.email || '-'}</a>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Phone</span>
                      <a href="tel:{client.phone}" class="detail-value">{client.phone || '-'}</a>
                    </div>
                  </div>
                </details>
              {/if}

              <!-- Quick Actions -->
              <div class="actions">
                <button class="action-btn primary" on:click|stopPropagation={() => handleAddTask(project.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 4v16m-8-8h16" />
                  </svg>
                  Add Task
                </button>
                <button class="action-btn" on:click|stopPropagation={() => handleMessage(project.id, client?.id)}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  Message
                </button>
              </div>
            </div>
          {/if}
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .project-hub {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  /* Command Bar */
  .command-bar {
    display: flex;
    gap: 12px;
    align-items: center;
    flex-wrap: wrap;
  }

  .search-box {
    flex: 1;
    min-width: 200px;
    max-width: 320px;
    position: relative;
  }

  .search-box svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: var(--text-muted);
  }

  .search-box input {
    padding-left: 38px;
    height: 36px;
  }

  .filters {
    display: flex;
    gap: 4px;
    background: var(--bg);
    padding: 3px;
    border-radius: var(--radius);
    border: 1px solid var(--border);
  }

  .filters button {
    padding: 6px 12px;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-muted);
    background: transparent;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    transition: all 100ms;
  }

  .filters button:hover { color: var(--text); }
  .filters button.active {
    background: #fff;
    color: var(--text);
    box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  }

  /* Stats Row */
  .stats-row {
    display: flex;
    gap: 16px;
    padding: 12px 0;
    border-bottom: 1px solid var(--border);
  }

  .stat {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .stat-value {
    font-size: 18px;
    font-weight: 600;
    color: var(--text);
    font-variant-numeric: tabular-nums;
  }

  .stat-label {
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }

  /* Loading & Empty States */
  .loading-state, .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 24px;
    color: var(--text-muted);
  }

  .empty-state svg {
    width: 48px;
    height: 48px;
    margin-bottom: 16px;
    opacity: 0.3;
  }

  /* Project Grid */
  .project-grid {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  /* Project Card */
  .project-card {
    background: var(--bg-card);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    transition: border-color 150ms, box-shadow 150ms;
  }

  .project-card:hover {
    border-color: #cbd5e1;
  }

  .project-card.expanded {
    border-color: var(--accent);
    box-shadow: 0 0 0 1px var(--accent);
  }

  /* Card Header */
  .card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    cursor: pointer;
    user-select: none;
  }

  .header-main {
    flex: 1;
    min-width: 0;
  }

  .project-title {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .project-title h3 {
    font-size: 14px;
    font-weight: 600;
    color: var(--text);
    margin: 0;
  }

  .project-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 4px;
  }

  .client-tag {
    font-size: 12px;
    color: var(--text-muted);
  }

  .wo-count {
    font-size: 11px;
    color: var(--accent);
    font-weight: 500;
  }

  .header-right {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  /* Progress Ring */
  .progress-ring {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: conic-gradient(
      var(--accent) calc(var(--progress) * 3.6deg),
      var(--border) calc(var(--progress) * 3.6deg)
    );
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }

  .progress-ring::before {
    content: '';
    position: absolute;
    inset: 4px;
    background: var(--bg-card);
    border-radius: 50%;
  }

  .progress-ring span {
    position: relative;
    font-size: 10px;
    font-weight: 600;
    color: var(--text);
  }

  .expand-icon {
    width: 16px;
    height: 16px;
    color: var(--text-muted);
    transition: transform 150ms;
  }

  .expand-icon.rotated {
    transform: rotate(180deg);
  }

  /* Status Badge */
  .status-badge {
    font-size: 10px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 99px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .status-success { background: #dcfce7; color: #166534; }
  .status-active { background: #dbeafe; color: #1e40af; }
  .status-warning { background: #fef3c7; color: #92400e; }
  .status-default { background: #f1f5f9; color: #475569; }

  /* Card Content */
  .card-content {
    padding: 0 16px 16px;
    border-top: 1px solid var(--border);
    margin-top: 0;
  }

  /* Metrics Grid */
  .metrics-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    padding: 14px 0;
    border-bottom: 1px solid var(--border);
  }

  .metric {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .metric-label {
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
  }

  .metric-value {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
  }

  .inline-select {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 4px 8px;
    cursor: pointer;
    max-width: 140px;
  }

  .inline-select:hover {
    border-color: var(--accent);
  }

  .inline-select:focus {
    outline: none;
    border-color: var(--accent);
  }

  /* Section */
  .section {
    padding: 14px 0;
    border-bottom: 1px solid var(--border);
  }

  .section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  .section-header h4 {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    margin: 0;
  }

  .section-count {
    font-size: 11px;
    font-weight: 600;
    color: var(--text-muted);
    background: var(--bg);
    padding: 2px 6px;
    border-radius: 4px;
  }

  /* Work Order List */
  .wo-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .wo-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    background: var(--bg);
    border-radius: 4px;
    gap: 12px;
  }

  .wo-main {
    flex: 1;
    min-width: 0;
  }

  .wo-title {
    font-size: 13px;
    font-weight: 500;
    color: var(--text);
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .wo-meta {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 2px;
    font-size: 11px;
    color: var(--text-muted);
  }

  .status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
  }

  .status-dot.status-success { background: #22c55e; }
  .status-dot.status-active { background: #3b82f6; }
  .status-dot.status-warning { background: #f59e0b; }
  .status-dot.status-default { background: #94a3b8; }

  .priority-tag {
    font-size: 9px;
    font-weight: 600;
    padding: 1px 4px;
    border-radius: 3px;
    text-transform: uppercase;
  }

  .priority-urgent { background: #fee2e2; color: #991b1b; }
  .priority-high { background: #fef3c7; color: #92400e; }
  .priority-normal { background: #f1f5f9; color: #64748b; }

  /* Work Order Actions */
  .wo-actions {
    display: flex;
    gap: 4px;
    opacity: 0;
    transition: opacity 100ms;
  }

  .wo-item:hover .wo-actions {
    opacity: 1;
  }

  .wo-action {
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 4px;
    color: var(--text-muted);
    cursor: pointer;
    transition: all 100ms;
  }

  .wo-action:hover {
    background: #fff;
    border-color: var(--border);
    color: var(--text);
  }

  .wo-action.complete:hover {
    background: #dcfce7;
    border-color: #86efac;
    color: #166534;
  }

  .wo-action svg {
    width: 14px;
    height: 14px;
  }

  .more-link {
    font-size: 12px;
    color: var(--accent);
    padding: 6px 0;
    cursor: pointer;
  }

  .empty-section {
    font-size: 12px;
    color: var(--text-muted);
    padding: 12px 0;
    text-align: center;
  }

  /* Details Section */
  .details-section {
    padding: 10px 0;
  }

  .details-section summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: var(--text-muted);
    cursor: pointer;
    padding: 8px 10px;
    list-style: none;
    background: var(--bg);
    border-radius: 6px;
    border: 1px solid var(--border);
    transition: all 150ms;
  }

  .details-section summary:hover {
    background: #fff;
    border-color: var(--accent);
    color: var(--text);
  }

  .details-section summary::-webkit-details-marker {
    display: none;
  }

  .summary-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .tap-hint {
    font-size: 9px;
    font-weight: 400;
    color: var(--text-muted);
    opacity: 0.7;
    text-transform: lowercase;
  }

  .details-section summary:hover .tap-hint {
    opacity: 0;
  }

  .details-section summary .chevron {
    width: 14px;
    height: 14px;
    transition: transform 150ms;
  }

  .details-section[open] summary {
    border-color: var(--accent);
    background: #fff;
  }

  .details-section[open] summary .tap-hint {
    display: none;
  }

  .details-section[open] summary .chevron {
    transform: rotate(180deg);
  }

  .details-content {
    padding-top: 10px;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
  }

  .detail-label {
    font-size: 11px;
    color: var(--text-muted);
  }

  .detail-value {
    font-size: 12px;
    color: var(--text);
    text-decoration: none;
  }

  .detail-value:hover {
    color: var(--accent);
  }

  /* Actions */
  .actions {
    display: flex;
    gap: 8px;
    padding-top: 14px;
  }

  .action-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 12px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: #fff;
    color: var(--text);
    cursor: pointer;
    transition: all 100ms;
  }

  .action-btn:hover {
    background: var(--bg);
  }

  .action-btn.primary {
    background: var(--primary);
    color: #fff;
    border-color: var(--primary);
  }

  .action-btn.primary:hover {
    background: var(--primary-hover);
  }

  .action-btn svg {
    width: 14px;
    height: 14px;
  }

  /* Mobile Responsive */
  @media (max-width: 767px) {
    .command-bar {
      flex-direction: column;
      align-items: stretch;
    }

    .search-box {
      max-width: none;
    }

    .filters {
      justify-content: center;
    }

    .stats-row {
      justify-content: space-between;
    }

    .stat-value {
      font-size: 16px;
    }

    .metrics-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .header-right {
      gap: 8px;
    }

    .progress-ring {
      width: 36px;
      height: 36px;
    }

    .actions {
      flex-direction: column;
    }
  }
</style>
